import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollTopButton from "@/components/feature/ScrollTopButton";
import ComicCard from "@/components/base/ComicCard";
import { comics, getByCategory } from "@/mocks/comics";
import { categories } from "@/mocks/categories";

type SortKey = "hot" | "score" | "episodes";

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "hot", label: "综合热度" },
  { key: "score", label: "评分最高" },
  { key: "episodes", label: "章节最多" },
];

const collectionLd = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: "天天漫画网 - 漫库列表",
  url: "https://panelplay1.github.io/list",
  inLanguage: "zh-CN",
  description: "浏览天天漫画网全部连载漫画，支持按分类筛选、关键词搜索与排序。",
};

export default function ListPage() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "all";
  const q = params.get("q") || "";
  const [keyword, setKeyword] = useState(q);
  const [sort, setSort] = useState<SortKey>("hot");

  const catName =
    category === "all" ? "全部漫画" : categories.find((c) => c.id === category)?.name || "全部漫画";

  const filtered = useMemo(() => {
    let list = getByCategory(category);
    if (q) {
      const k = q.toLowerCase();
      list = list.filter(
        (c) =>
          c.title.toLowerCase().includes(k) ||
          c.altTitle.toLowerCase().includes(k) ||
          c.tags.some((t) => t.toLowerCase().includes(k)),
      );
    }
    if (sort === "score") list = [...list].sort((a, b) => b.rating - a.rating);
    else if (sort === "episodes") list = [...list].sort((a, b) => b.episodes - a.episodes);
    else list = [...list].sort((a, b) => b.rating - a.rating);
    return list;
  }, [category, q, sort]);

  const setCategory = (id: string) => {
    const next = new URLSearchParams(params);
    if (id === "all") next.delete("category");
    else next.set("category", id);
    setParams(next, { replace: true });
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (keyword.trim()) next.set("q", keyword.trim());
    else next.delete("q");
    setParams(next, { replace: true });
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(collectionLd) }} />
      <Navbar />
      <main className="mx-auto max-w-7xl px-4 py-8 md:px-6">
        <nav className="mb-6 flex items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
          <Link to="/" className="hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-900">{catName}</span>
        </nav>

        <h1 className="font-heading text-2xl text-foreground-950 md:text-3xl">{catName} · 漫库</h1>
        <p className="mt-2 text-sm text-foreground-600">
          共 {filtered.length} 部作品，每日更新最新话，追更漫画不迷路。
        </p>

        <div className="mt-6 flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setCategory("all")}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                category === "all"
                  ? "bg-primary-500 font-bold text-background-50"
                  : "border border-background-300 text-foreground-600 hover:border-primary-400 hover:text-primary-600"
              }`}
            >
              全部
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setCategory(c.id)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                  category === c.id
                    ? "bg-primary-500 font-bold text-background-50"
                    : "border border-background-300 text-foreground-600 hover:border-primary-400 hover:text-primary-600"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>

          <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
            <form onSubmit={handleSearch} className="flex items-center overflow-hidden rounded-full border border-background-300 bg-background-50">
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索漫画名称或标签"
                className="w-full bg-transparent px-4 py-2 text-sm outline-none sm:w-72"
                aria-label="搜索漫画"
              />
              <button type="submit" aria-label="搜索" className="flex h-9 w-10 shrink-0 items-center justify-center rounded-full bg-primary-500 text-background-50 transition-colors hover:bg-primary-600">
                <i className="ri-search-line text-sm"></i>
              </button>
            </form>
            <div className="flex items-center gap-1 rounded-full border border-background-300 bg-background-50 p-1">
              {sortOptions.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setSort(s.key)}
                  className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
                    sort === s.key ? "bg-secondary-500 font-bold text-background-50" : "text-foreground-600 hover:text-primary-600"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <i className="ri-inbox-2-line text-4xl text-foreground-300"></i>
            <p className="text-sm text-foreground-600">没有找到相关漫画，换个关键词试试吧</p>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {filtered.map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
          </div>
        )}
      </main>
      <Footer />
      <ScrollTopButton />
    </>
  );
}